import type {
  ApiErrorBody,
  ApiErrorCode,
  ApplySyncBaselineResponse,
  CanvasMetadata,
  ChangeScriptResponse,
  ConnectionInput,
  CreateCheckpointResponse,
  CreateUserResponse,
  DialectDescriptor,
  DialectId,
  DialectTypeCatalog,
  EditLeaseResponse,
  GenerateChangeScriptResponse,
  GenerateCreateScriptResponse,
  GetSyncBaselineResponse,
  HistoryDiffResponse,
  HistoryEntrySummary,
  ImportJobStatus,
  ListDuckdbFilesResponse,
  ListUsersResponse,
  MeResponse,
  ProjectFile,
  ProjectSummary,
  RevertResponse,
  SchemaModel,
  StartImportRequest,
  StartSyncScanResponse,
  SyncScanJobStatus,
  TestConnectionResponse,
  UserSummary,
} from '@bits2schema/shared';

/** Chyba API se strojově čitelným kódem — text k němu dohledá i18n vrstva. */
export class ApiError extends Error {
  readonly code: ApiErrorCode;

  constructor(code: ApiErrorCode, message: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const body = payload as ApiErrorBody | null;
    throw new ApiError(
      body?.error?.code ?? 'internalError',
      body?.error?.message ?? response.statusText,
    );
  }
  return payload as T;
}

function post<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

export const api = {
  listDialects: () => request<{ dialects: DialectDescriptor[] }>('/dialects'),

  getTypeCatalog: (dialectId: DialectId) =>
    request<{ catalog: DialectTypeCatalog }>(`/dialects/${dialectId}/type-catalog`),

  testConnection: (dialect: DialectId, connection: ConnectionInput) =>
    post<TestConnectionResponse>('/connections/test', { dialect, connection }),

  listSchemas: (dialect: DialectId, connection: ConnectionInput) =>
    post<{ schemas: string[] }>('/connections/schemas', { dialect, connection }),

  listDuckdbFiles: () => request<ListDuckdbFilesResponse>('/connections/duckdb-files'),

  listProjects: () => request<{ projects: ProjectSummary[] }>('/projects'),

  createGreenfieldProject: (name: string, dialect: DialectId) =>
    post<{ project: ProjectFile }>('/projects/greenfield', { name, dialect }),

  getProject: (projectId: string) => request<{ project: ProjectFile }>(`/projects/${projectId}`),

  openProject: (projectId: string) =>
    post<{ project: ProjectFile }>(`/projects/${projectId}/open`, {}),

  attachConnection: (projectId: string, connection: ConnectionInput) =>
    request<{ project: ProjectFile }>(`/projects/${projectId}/connection`, {
      method: 'PUT',
      body: JSON.stringify({ connection }),
    }),

  deleteProject: (projectId: string) =>
    request<void>(`/projects/${projectId}`, { method: 'DELETE' }),

  // Epic 15 — rozšíření možností projektů.
  renameProject: (projectId: string, name: string) =>
    request<{ project: ProjectFile }>(`/projects/${projectId}/name`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    }),

  generateCreateScript: (projectId: string, schemaName?: string) =>
    post<GenerateCreateScriptResponse>(`/projects/${projectId}/create-script`, schemaName ? { schemaName } : {}),

  duplicateProject: (projectId: string, name: string) =>
    post<{ project: ProjectFile }>(`/projects/${projectId}/duplicate`, { name }),

  updateCanvas: (projectId: string, canvas: CanvasMetadata) =>
    request<{ project: ProjectFile }>(`/projects/${projectId}/canvas`, {
      method: 'PUT',
      body: JSON.stringify({ canvas }),
    }),

  updateSchema: (projectId: string, schema: SchemaModel) =>
    request<{ project: ProjectFile }>(`/projects/${projectId}/schema`, {
      method: 'PUT',
      body: JSON.stringify({ schema }),
    }),

  getHistory: (projectId: string) =>
    request<{ entries: HistoryEntrySummary[] }>(`/projects/${projectId}/history`),

  createCheckpoint: (projectId: string, label?: string) =>
    post<CreateCheckpointResponse>(`/projects/${projectId}/checkpoints`, label ? { label } : {}),

  getHistoryDiff: (projectId: string, fromId: string, toId: string) =>
    request<HistoryDiffResponse>(
      `/projects/${projectId}/history/diff?from=${encodeURIComponent(fromId)}&to=${encodeURIComponent(toId)}`,
    ),

  revertHistoryEntry: (projectId: string, entryId: string) =>
    post<RevertResponse>(`/projects/${projectId}/history/${entryId}/revert`, {}),

  startImport: (payload: StartImportRequest) => post<{ jobId: string }>('/imports', payload),

  getImportJob: (jobId: string) => request<ImportJobStatus>(`/imports/${jobId}`),

  startSyncScan: (projectId: string) =>
    post<StartSyncScanResponse>(`/projects/${projectId}/sync/scan`, {}),

  getSyncScanJob: (projectId: string, jobId: string) =>
    request<SyncScanJobStatus>(`/projects/${projectId}/sync/scan/${jobId}`),

  applySyncBaseline: (
    projectId: string,
    schema: SchemaModel,
    canvas: CanvasMetadata,
    label?: string,
    baselineSchema?: SchemaModel,
  ) =>
    post<ApplySyncBaselineResponse>(`/projects/${projectId}/sync/baseline`, {
      schema,
      canvas,
      ...(label ? { label } : {}),
      ...(baselineSchema ? { baselineSchema } : {}),
    }),

  getSyncBaseline: (projectId: string) => request<GetSyncBaselineResponse>(`/projects/${projectId}/sync/baseline`),

  generateChangeScript: (projectId: string, selectedKeys: string[]) =>
    post<GenerateChangeScriptResponse>(`/projects/${projectId}/change-script/generate`, { selectedKeys }),

  getChangeScript: (projectId: string, entryId: string) =>
    request<ChangeScriptResponse>(`/projects/${projectId}/history/${entryId}/change-script`),

  // Epic 14 — uživatelé a oprávnění.
  login: (username: string, password: string) => post<MeResponse>('/auth/login', { username, password }),

  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  me: () => request<MeResponse>('/auth/me'),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/auth/password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  listUsers: () => request<ListUsersResponse>('/users'),

  createUser: (username: string, displayName: string, password: string) =>
    post<CreateUserResponse>('/users', { username, displayName, password }),

  addEditor: (projectId: string, userId: string) =>
    post<{ project: ProjectFile }>(`/projects/${projectId}/editors`, { userId }),

  removeEditor: (projectId: string, userId: string) =>
    request<{ project: ProjectFile }>(`/projects/${projectId}/editors/${userId}`, { method: 'DELETE' }),

  transferOwnership: (projectId: string, newCreatorId: string) =>
    post<{ project: ProjectFile }>(`/projects/${projectId}/transfer-ownership`, { newCreatorId }),

  getLease: (projectId: string) => request<EditLeaseResponse>(`/projects/${projectId}/lease`),

  acquireLease: (projectId: string) => post<EditLeaseResponse>(`/projects/${projectId}/lease`, {}),

  takeoverLease: (projectId: string) => post<EditLeaseResponse>(`/projects/${projectId}/lease/takeover`, {}),

  releaseLease: (projectId: string) => request<void>(`/projects/${projectId}/lease`, { method: 'DELETE' }),
};

export type { UserSummary };
